import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import { IntlProvider } from "react-intl";
import en from "./intl/en.json";
import { AppProvider } from "./components/AppProvider/AppProvider.tsx";
import { useDispatch, useSelector, useStore } from "react-redux";
import { Auth } from "aws-amplify";
import { toast } from "react-toastify";
import { authAction } from "./redux/actions/authActions.ts";
import {
  logout,
  setAuthIdle,
  setAuthLoading,
} from "./redux/slices/authSlice.ts";
import { logoutUser } from "./redux/slices/userInfoStore.ts";
import {
  getIsAuthComplete,
  getIsAuthPending,
} from "./redux/selectors/authSelectors.ts";
import { hasOAuthResponseInUrl } from "./utils/oauthCallback.ts";
import { RootReducerState } from "./utils/types.ts";
import router, { navigation } from "./routes/router.tsx";
import theme from "./styles/themes/themePrimitives.ts";
import { Box } from "@mui/material";
import fpLogo from "./styles/assets/logo_fpone.png";
import { datadogRum } from "@datadog/browser-rum";
import { checkVersion } from "./utils/checkVersion.ts";
import { registerSignOut } from "./utils/authBridge";

const LOCALE = "en";
const SESSION_CHECK_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

interface DemoProps {
  /**
   * Injected by the documentation to work in an iframe.
   * Remove this when copying and pasting into your project.
   */
  window?: () => Window;
}

const App: React.FC = (props: DemoProps) => {
  const dispatch = useDispatch();
  const store = useStore();
  const { window } = props;
  const demoWindow = window !== undefined ? window() : undefined;

  const [isNewVersionAvailable, setIsNewVersionAvailable] = useState(false);

  // Boot-time auth bootstrap: resolve the INITIALIZING state exactly once.
  // - OAuth redirect in the URL: Amplify is processing it; keep the loading
  //   state and let the Hub "signIn" listener (index.tsx) finish the job.
  // - Otherwise: silently restore an existing Cognito session, or settle on
  //   IDLE so the Login page is shown. No breadcrumbs, no races.
  const bootstrapStarted = useRef(false);
  useEffect(() => {
    if (bootstrapStarted.current) return; // StrictMode double-invoke guard
    bootstrapStarted.current = true;

    if (hasOAuthResponseInUrl()) {
      dispatch(setAuthLoading());
      return;
    }

    Auth.currentSession()
      .then(() => dispatch(authAction({})))
      .catch(() => dispatch(setAuthIdle()));
  }, [dispatch]);

  const currentAuthenticatedUser = useSelector(
    (state: any) => state.user.currentAuthenticatedUser,
  );

  useEffect(() => {
    if (currentAuthenticatedUser) {
      datadogRum.setUser({
        id: currentAuthenticatedUser,
        name: currentAuthenticatedUser,
        email: currentAuthenticatedUser,
      });
      datadogRum.startSessionReplayRecording();
      console.log(
        "Datadog RUM: user set, replay started for %o",
        currentAuthenticatedUser,
      );
    } else {
      datadogRum.clearUser();
      datadogRum.stopSessionReplayRecording();
      console.log(
        "Datadog RUM: user cleared, replay stopped for %o",
        currentAuthenticatedUser,
      );
    }
  }, [currentAuthenticatedUser]);

  const authentication = React.useMemo(() => {
    return {
      checkSession: () => {
        Auth.currentSession()
          .then((data) => {
            const accessTokenExpiry = data.getAccessToken().getExpiration();
            const currentTimeSeconds = Math.round(+new Date() / 1000);
            console.log(
              "Token expires in",
              (accessTokenExpiry - currentTimeSeconds) * 0.0166667,
              "minutes",
            );
            dispatch(authAction({}));
          })
          .catch((error) => {
            console.log("No current user: ", error);
          });
      },
      signIn: () => {
        // Add proper implementation to handle successful sign-in
        dispatch(authAction({}));
        // Consider also setting user info in state if needed
      },
      signOut: async () => {
        // Clear app state first (instant UI feedback), then let Amplify do
        // the real sign-out: revoke tokens server-side (global) and redirect
        // through Cognito's /logout to clear its hosted-UI session cookie.
        // The browser navigates away and returns signed out; the boot
        // bootstrap then lands on Login. Note: this cannot end the
        // Microsoft 365 SSO session itself — that would sign the user out
        // of Outlook/Teams too.
        dispatch(logout());
        dispatch(logoutUser());
        try {
          await Auth.signOut({ global: true });
        } catch (error) {
          // Global revoke can fail (e.g. token already expired) — still
          // clear the local + Cognito session.
          console.error("Global sign-out failed, falling back: ", error);
          await Auth.signOut().catch((e) =>
            console.error("Sign-out failed: ", e),
          );
        }
      },
    };
  }, [dispatch]);

  // Register logout with the bridge so axios can call it
  useEffect(() => {
    registerSignOut(authentication.signOut);
  }, [authentication]);

  // Session check on every tab return. Fires the moment the portal tab becomes
  // visible — independent of any API call. Valid session: fresh token stored in
  // localStorage ready for the next request. Expired: clear state + toast so
  // the user sees Login immediately rather than mid-flow after an API 401.
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;
      // Only check if currently authenticated — skip for signed-out / initializing
      // states so we don't fire a spurious "session expired" toast on Login.
      if (!getIsAuthComplete(store.getState() as RootReducerState)) return;

      try {
        const session = await Auth.currentSession();
        localStorage.setItem(
          "accessToken",
          session.getAccessToken().getJwtToken(),
        );
      } catch {
        // Token expired and Amplify can't refresh it (no refresh token on
        // implicit flow). Clear state now so the user sees Login immediately
        // rather than after the next API call fails.
        dispatch(logout());
        dispatch(logoutUser());
        toast.warning("Your session has expired. Please sign in again.", {
          toastId: "session-expired",
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [dispatch, store]);

  // Periodic background session check. Catches tokens that expire while the
  // user stays on the portal tab without switching away (visibilitychange
  // wouldn't fire in that case). Auth.currentSession() is a local expiry check
  // — no network call for a valid token.
  useEffect(() => {
    const checkSession = async () => {
      if (!getIsAuthComplete(store.getState() as RootReducerState)) return;
      try {
        const session = await Auth.currentSession();
        localStorage.setItem(
          "accessToken",
          session.getAccessToken().getJwtToken(),
        );
      } catch {
        dispatch(logout());
        dispatch(logoutUser());
        toast.warning("Your session has expired. Please sign in again.", {
          toastId: "session-expired",
        });
      }
    };

    const interval = setInterval(checkSession, SESSION_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [dispatch, store]);

  // Cross-tab sign-out: storage events fire in every tab EXCEPT the one that
  // made the change. When Tab A's logoutUser() removes accessToken, Tab B hears
  // it here immediately and mirrors the sign-out — no waiting for the next
  // periodic check or tab switch.
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== "accessToken" || event.newValue !== null) return;
      if (!getIsAuthComplete(store.getState() as RootReducerState)) return;
      dispatch(logout());
      dispatch(logoutUser());
      toast.info("You were signed out in another tab.", {
        toastId: "cross-tab-signout",
      });
    };

    // globalThis avoids the `window` prop shadow in this component
    globalThis.addEventListener("storage", handleStorageChange);
    return () => globalThis.removeEventListener("storage", handleStorageChange);
  }, [dispatch, store]);

  useEffect(() => {
    // Add an event listener for interactions (e.g., clicks)
    const handleInteraction = async () => {
      // Never reload mid-sign-in: a reload here cancels the OAuth redirect
      // or drops the callback that is being processed.
      if (
        hasOAuthResponseInUrl() ||
        getIsAuthPending(store.getState() as RootReducerState)
      ) {
        return;
      }
      const isNew = await checkVersion();
      console.log("versionCheck: %o", isNew);
      setIsNewVersionAvailable(isNew);
      if (isNew) {
        (window as unknown as Window).location.reload();
      }
    };

    // Attach the event listener to the document
    document.addEventListener("click", handleInteraction);

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener("click", handleInteraction);
    };
  }, [store]);

  return (
    <IntlProvider locale={LOCALE} messages={en}>
      <AppProvider
        authentication={authentication}
        navigation={navigation}
        router={router}
        window={demoWindow}
        theme={theme}
        // theme={null}
        branding={{
          logo: (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}
            >
              <img src={fpLogo} alt="FreightPlus Logo" width={200} />
            </Box>
          ),
          title: "",
        }}
      />
    </IntlProvider>
  );
};

export default App;
